import React, { useEffect, useState } from "react";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import { useDispatch, useSelector } from "react-redux";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import {
  UpdateCustomerPaymentApi,
  UpdateSupplierPaymentApi,
} from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import CustomerSelector from "../Selector/CustomerSelector";
import CompanySelector from "../Selector/CompanySelector";
import { fetchCustomers } from "../../store/Slices/CustomerSlice";
import { fetchSuppliers } from "../../store/Slices/SupplierSlice";
import { fetchAccounts } from "../../store/Slices/AccountSlice";
import PaymentAccountSelector from "../Selector/PaymentAccountSelector";
import { toLocalDateTimeInput } from "../../utils/dateTime";

const toDateInputValue = (date) => {
  if (date == null || date === "") {
    return toLocalDateTimeInput();
  }
  if (typeof date === "number" && Number.isFinite(date)) {
    const ms = date > 1e12 ? date : date * 1000;
    return toLocalDateTimeInput(ms);
  }
  const s = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.slice(0, 16);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    // Date-only legacy value: keep calendar day, use current clock time
    return toLocalDateTimeInput(s.slice(0, 10));
  }
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 1e9) {
    const ms = asNum > 1e12 ? asNum : asNum * 1000;
    return toLocalDateTimeInput(ms);
  }
  return toLocalDateTimeInput();
};

const resolveCustomer = (payment) => {
  const c = payment?.customerId;
  if (c && typeof c === "object" && c._id) return c;
  if (c) {
    return {
      _id: c,
      name: payment?.customer_name || payment?.name || "",
    };
  }
  return "";
};

const resolveCompany = (payment) => {
  const c = payment?.companyId || payment?.company_id;
  if (c && typeof c === "object" && c._id) return c;
  if (c) {
    return {
      _id: c,
      name: payment?.company_name || payment?.name || "",
    };
  }
  return "";
};

const resolveAccount = (payment, accounts = []) => {
  const acc = payment?.accountId;
  if (acc && typeof acc === "object" && (acc._id || acc.id)) {
    const id = acc._id || acc.id;
    const matched = (accounts || []).find(
      (a) => String(a._id) === String(id)
    );
    return matched || { ...acc, _id: id };
  }
  if (acc) {
    const matched = (accounts || []).find(
      (a) => String(a._id) === String(acc)
    );
    if (matched) return matched;
  }
  // Legacy rows: match bank / cash without accountId
  const bankName = String(payment?.bank_name || "").trim().toLowerCase();
  if (bankName && accounts.length) {
    const byName = accounts.find(
      (a) => String(a.account_name || "").trim().toLowerCase() === bankName
    );
    if (byName) return byName;
  }
  if (Number(payment?.payment_type) === 1 && accounts.length) {
    const cash = accounts.find((a) => Number(a.type) === 1);
    if (cash) return cash;
  }
  return "";
};

const EditPaymentModal = ({
  Open,
  setOpen,
  paymentDetails,
  type,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const AccountState = useSelector((state) => state.AccountState);
  const accounts = Array.isArray(AccountState?.data) ? AccountState.data : [];

  const [SelectedCustomer, setSelectedCustomer] = useState(() =>
    resolveCustomer(paymentDetails)
  );
  const [SelectedSupplier, setSelectedSupplier] = useState(() =>
    resolveCompany(paymentDetails)
  );
  const [selectedAccount, setSelectedAccount] = useState(() =>
    resolveAccount(paymentDetails, [])
  );
  const [depositor, setDepositor] = useState(
    paymentDetails?.invoice_no != null ? String(paymentDetails.invoice_no) : ""
  );
  const [amount, setAmount] = useState(
    paymentDetails?.amount != null ? String(paymentDetails.amount) : ""
  );
  const [desc, setDesc] = useState(paymentDetails?.desc || "");
  const [CurDate, setCurDate] = useState(() =>
    toDateInputValue(paymentDetails?.date)
  );
  const [loading, setLoading] = useState(false);
  const [activeTab] = useState(type === "Company" ? "Company" : "Customer");

  useEffect(() => {
    if (!Open) return;
    dispatch(fetchAccounts());
    if (activeTab === "Customer") dispatch(fetchCustomers());
    if (activeTab === "Company") dispatch(fetchSuppliers());
  }, [Open, dispatch, activeTab]);

  // Hydrate form fields when modal opens / payment changes (not on every accounts refresh)
  useEffect(() => {
    if (!Open || !paymentDetails) return;
    setSelectedCustomer(resolveCustomer(paymentDetails));
    setSelectedSupplier(resolveCompany(paymentDetails));
    setSelectedAccount(resolveAccount(paymentDetails, accounts));
    setDepositor(
      paymentDetails.invoice_no != null
        ? String(paymentDetails.invoice_no)
        : ""
    );
    setAmount(
      paymentDetails.amount != null ? String(paymentDetails.amount) : ""
    );
    setDesc(paymentDetails.desc || "");
    setCurDate(toDateInputValue(paymentDetails.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-hydrate on open/payment
  }, [Open, paymentDetails]);

  // When accounts arrive, fill account if still empty / only id (don't wipe user pick)
  useEffect(() => {
    if (!Open || !paymentDetails || !accounts.length) return;
    setSelectedAccount((prev) => {
      if (prev?._id && prev.account_name) {
        const fresh = accounts.find(
          (a) => String(a._id) === String(prev._id)
        );
        return fresh || prev;
      }
      return resolveAccount(paymentDetails, accounts) || prev;
    });
  }, [Open, paymentDetails, accounts]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const paymentTypeNum = Number(selectedAccount?.type) === 1 ? 1 : 2;
    const payload = {
      invoice_no: depositor,
      payment_type: paymentTypeNum,
      bank_name: paymentTypeNum === 2 ? selectedAccount.account_name : "",
      bank_number: paymentTypeNum === 2 ? selectedAccount.account_no : "",
      accountId: selectedAccount._id,
      desc,
      amount: Number(amount),
      date: CurDate,
    };

    if (activeTab === "Customer" && !SelectedCustomer?._id) {
      WarningToast("Select a Customer");
    } else if (activeTab === "Company" && !SelectedSupplier?._id) {
      WarningToast("Select a Company");
    } else if (!depositor) {
      WarningToast("Enter Bill No");
    } else if (!selectedAccount?._id) {
      WarningToast("Select payment account");
    } else if (!amount || isNaN(amount)) {
      WarningToast("Enter a valid Amount");
    } else if (!CurDate) {
      WarningToast("Select a valid Date");
    } else {
      try {
        let response;
        if (activeTab === "Customer") {
          response = await UpdateCustomerPaymentApi(paymentDetails._id, {
            ...payload,
            customerId: SelectedCustomer._id,
            customer_name: SelectedCustomer.name,
          });
        } else {
          response = await UpdateSupplierPaymentApi(paymentDetails._id, {
            ...payload,
            companyId: SelectedSupplier._id,
            company_name: SelectedSupplier.name,
          });
        }

        if (response.data.success) {
          setOpen(false);
          SuccessToast("Payment updated successfully");
          if (activeTab === "Customer") dispatch(fetchCustomers());
          if (activeTab === "Company") dispatch(fetchSuppliers());
          dispatch(fetchAccounts());
          onSuccess?.();
        } else {
          ErrorToast(response.data.error?.msg);
        }
      } catch (err) {
        console.error(err);
        ErrorToast(
          err?.response?.data?.error?.msg || "Failed to update payment"
        );
      }
    }
    setLoading(false);
  };

  return (
    <CustomModal open={Open} setOpen={setOpen}>
      <div className="pb-8">
        <h1 className="w-full text-center font-[700] text-3xl py-8">
          Edit Payment
        </h1>
        <div className="flex gap-4 max696:flex-col px-10 max696:max-h-[70vh] max696:overflow-y-auto">
          <div className="flex flex-col gap-y-4">
            {activeTab === "Customer" ? (
              <CustomerSelector
                SelectedCustomer={SelectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
            ) : (
              <CompanySelector
                SelectedSupplier={SelectedSupplier}
                setSelectedSupplier={setSelectedSupplier}
              />
            )}
            <PaymentAccountSelector
              label="Payment account"
              selectedAccount={selectedAccount}
              setSelectedAccount={setSelectedAccount}
            />

            <AuthInput
              label="Description"
              placeholder="Enter Description"
              Value={desc}
              setValue={setDesc}
            />
          </div>
          <div className="flex flex-col gap-y-4">
            <AuthInput
              label="Date & Time"
              placeholder="Select Date & Time"
              Type="datetime-local"
              Value={CurDate}
              setValue={setCurDate}
            />

            <AuthInput
              label="Bill No"
              placeholder="Enter Bill No"
              Value={depositor}
              setValue={setDepositor}
              Type={"number"}
            />

            <AuthInput
              label="Amount"
              placeholder="Enter Amount"
              Type="number"
              Value={amount}
              setValue={setAmount}
            />
          </div>
        </div>
        <div className="flex justify-center items-center gap-x-2 mt-5 px-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn title={"Update Payment"} onSubmit={onSubmit} type={1} />
              <AuthBtn
                title={"Cancel"}
                onSubmit={() => setOpen(false)}
                type={2}
              />
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default EditPaymentModal;
